/** Per-account opt-in to sync provider folders/labels into the Mail nav. */

import { api } from "@/lib/api";

export const MAIL_SYNC_FOLDERS_KEY = "atw-hub-mail-sync-provider-folders";
export const MAIL_SYNC_FOLDERS_EVENT = "atw-hub-mail-sync-provider-folders";

export type MailProviderFolder = {
  id: string;
  label: string;
  type?: string;
  unread?: number;
};

type SyncMap = Record<string, boolean>;

function readMap(): SyncMap {
  try {
    const raw = localStorage.getItem(MAIL_SYNC_FOLDERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: SyncMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "boolean") out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: SyncMap) {
  try {
    localStorage.setItem(MAIL_SYNC_FOLDERS_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
  window.dispatchEvent(new CustomEvent(MAIL_SYNC_FOLDERS_EVENT, { detail: map }));
}

/** Opt-in: default false — keep standard Inbox/Sent/Drafts/Archive unless enabled. */
export function isAccountFolderSyncEnabled(accountId: string): boolean {
  if (!accountId) return false;
  return Boolean(readMap()[accountId]);
}

export function setAccountFolderSyncEnabled(accountId: string, enabled: boolean) {
  if (!accountId) return;
  const map = readMap();
  if (enabled) map[accountId] = true;
  else delete map[accountId];
  writeMap(map);
}

export function readFolderSyncMap(): SyncMap {
  return readMap();
}

/** Standard Hub folders when sync is off or provider list is empty. */
export const STANDARD_MAIL_FOLDERS: MailProviderFolder[] = [
  { id: "inbox", label: "Inbox", type: "inbox" },
  { id: "starred", label: "Starred", type: "starred" },
  { id: "sent", label: "Sent", type: "sent" },
  { id: "drafts", label: "Drafts", type: "drafts" },
  { id: "archive", label: "Archive", type: "archive" },
  { id: "trash", label: "Trash", type: "trash" },
  { id: "junk", label: "Spam", type: "junk" },
];

/** Normalize client folder ids (spam ↔ junk) to the Hub/API id. */
export function normalizeMailFolderId(folder: string | null | undefined): string {
  const value = String(folder || "inbox").trim().toLowerCase();
  if (value === "spam") return "junk";
  if (value === "deleted") return "trash";
  return value || "inbox";
}

export async function fetchAccountFolders(accountId: string): Promise<MailProviderFolder[]> {
  if (!accountId) return [...STANDARD_MAIL_FOLDERS];
  try {
    const payload = await api<{ folders?: MailProviderFolder[] } | MailProviderFolder[]>(
      `/api/mail/folders?accountId=${encodeURIComponent(accountId)}`,
      { timeoutMs: 12_000 },
    );
    const list = Array.isArray(payload) ? payload : payload.folders || [];
    const cleaned = list
      .map((folder) => ({
        id: String(folder.id || "").trim(),
        label: String(folder.label || folder.id || "").trim(),
        type: folder.type ? String(folder.type) : undefined,
        unread: typeof folder.unread === "number" ? folder.unread : undefined,
      }))
      .filter((folder) => folder.id && folder.label);
    return cleaned.length ? cleaned : [...STANDARD_MAIL_FOLDERS];
  } catch {
    return [...STANDARD_MAIL_FOLDERS];
  }
}
